import { createContext, useContext, ReactNode, useState } from 'react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogCloseTrigger,
} from '@/components/ui/dialog';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

type ModalContextType = {
  showModal: (
    content: ReactNode,
    title?: string,
    size?: ModalSize,
    placement?: 'top' | 'center' | 'bottom',
    background?: string
  ) => void;
  hideModal: () => void;
  isOpen: boolean;
  content: ReactNode | null;
  title: string | null;
  size: ModalSize;
  placement: 'top' | 'center' | 'bottom';
  background: string | null;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [size, setSize] = useState<ModalSize>('md');
  const [placement, setPlacement] = useState<'top' | 'center' | 'bottom'>(
    'top'
  );
  const [background, setBackground] = useState<string | null>(null);

  const showModal = (
    content: ReactNode,
    title?: string,
    size: ModalSize = 'md',
    placement: 'top' | 'center' | 'bottom' = 'top',
    background?: string
  ) => {
    setContent(content);
    setTitle(title || null);
    setSize(size);
    setPlacement(placement);
    setBackground(background || null);
    setIsOpen(true);
  };

  const hideModal = () => {
    setIsOpen(false);
    setContent(null);
    setTitle(null);
    setBackground(null);
  };

  return (
    <ModalContext.Provider
      value={{
        showModal,
        hideModal,
        isOpen,
        content,
        title,
        size,
        placement,
        background,
      }}
    >
      {children}
      <DialogRoot
        open={isOpen}
        onOpenChange={(e) => !e.open && hideModal()}
        size={size}
        placement={placement}
      >
        <DialogContent bg={background || undefined}>
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
          </DialogHeader>
          <DialogBody>{content}</DialogBody>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
